import { ChevronDown, Info, Search as SearchIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AppShell } from "@/components/AppShell";
import { EmptyState } from "@/components/empty-state";
import { GuideNotesEditor } from "@/components/guides/GuideNotesEditor";
import { SearchInput } from "@/components/shared/SearchInput";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGuideCategories, useGuideEntries } from "@/hooks/api/guides";
import { resolveBilingual, type ResolvedText } from "@/lib/bilingual";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import type { GuideEntry, GuideEntryType, LanguagePreference } from "@/types/api";

const ALL = "all";
type TypeFilter = GuideEntryType | typeof ALL;

/** Same bilingual-block treatment as FlowPlayerPage — this content can be
 * read live mid-call, so a translation is never hidden behind a hover. */
function BilingualBlock({ text, className }: { text: ResolvedText; className: string }) {
  if (!text.secondary) {
    return <p className={className}>{text.primary}</p>;
  }
  return (
    <div className="space-y-2">
      <div>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          EN
        </span>
        <p className={className}>{text.primary}</p>
      </div>
      <div className="border-t border-slate-200 pt-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          ES
        </span>
        <p className={className}>{text.secondary}</p>
      </div>
    </div>
  );
}

export default function GuideListPage() {
  const { t } = useTranslation();
  const { data: entriesData, isLoading, error } = useGuideEntries();
  const { data: categoriesData } = useGuideCategories();
  const languagePreference = useAuthStore((s) => s.user?.language_preference) ?? "BOTH";

  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState<string>(ALL);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(ALL);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const entries = entriesData?.results ?? [];

  const filtered = useMemo(() => {
    return entries.filter((entry) => {
      const matchesCategory = categoryId === ALL || entry.category.id === categoryId;
      const matchesType = typeFilter === ALL || entry.entry_type === typeFilter;
      const q = query.trim().toLowerCase();
      const matchesQuery =
        q === "" ||
        [
          entry.title,
          entry.title_es,
          entry.spiel_text,
          entry.spiel_text_es,
          entry.internal_note,
          entry.internal_note_es,
        ].some((field) => field.toLowerCase().includes(q));
      return matchesCategory && matchesType && matchesQuery;
    });
  }, [entries, categoryId, typeFilter, query]);

  const categories = categoriesData?.results ?? [];
  const categoriesWithEntries = categories.filter((cat) =>
    entries.some((e) => e.category.id === cat.id)
  );

  return (
    <AppShell>
      <h1 className="m-0 text-2xl font-bold text-slate-900">{t("guideList.title")}</h1>
      <p className="mt-1 text-sm text-slate-600">{t("guideList.subtitle")}</p>

      <div className="mt-6 max-w-md">
        <SearchInput
          placeholder={t("guideList.searchPlaceholder")}
          onSearch={setQuery}
          debounceDelay={200}
          size="md"
        />
      </div>

      {/* Rebuttal vs. general guideline — separate from the category
       * filter below so "just the things I need to say" is one click
       * regardless of which category it's filed under. See
       * apps/guides/models.py::GuideEntry.entry_type. */}
      <Tabs
        value={typeFilter}
        onValueChange={(v) => setTypeFilter(v as TypeFilter)}
        className="mt-4"
      >
        <TabsList className="h-auto flex-wrap gap-2 border-none">
          <TabsTrigger value={ALL}>{t("guideList.allTypes")}</TabsTrigger>
          <TabsTrigger value="GUIDELINE">{t("guideList.typeGuideline")}</TabsTrigger>
          <TabsTrigger value="REBUTTAL">{t("guideList.typeRebuttal")}</TabsTrigger>
        </TabsList>
      </Tabs>

      {categoriesWithEntries.length > 0 && (
        <Tabs value={categoryId} onValueChange={setCategoryId} className="mt-4">
          <TabsList className="h-auto flex-wrap gap-2 border-none">
            <TabsTrigger value={ALL}>{t("guideList.allCategories")}</TabsTrigger>
            {categoriesWithEntries.map((cat) => {
              const label = resolveBilingual(languagePreference, {
                en: cat.name,
                es: cat.name_es,
              });
              return (
                <TabsTrigger key={cat.id} value={cat.id}>
                  {label.primary}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      )}

      <div className="mt-6">
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        )}

        {error && <p className="text-sm text-danger">{t("guideList.loadError")}</p>}

        {!isLoading && !error && entries.length === 0 && (
          <EmptyState
            icon={SearchIcon}
            title={t("guideList.noEntriesTitle")}
            description={t("guideList.noEntriesDescription")}
          />
        )}

        {!isLoading && !error && entries.length > 0 && filtered.length === 0 && (
          <EmptyState
            icon={SearchIcon}
            title={t("guideList.noMatchesTitle")}
            description={t("guideList.noMatchesDescription")}
          />
        )}

        {filtered.length > 0 && (
          <div className="space-y-2">
            {filtered.map((entry) => (
              <GuideCard
                key={entry.id}
                entry={entry}
                languagePreference={languagePreference}
                expanded={expandedId === entry.id}
                onToggle={() =>
                  setExpandedId((current) => (current === entry.id ? null : entry.id))
                }
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function GuideCard({
  entry,
  languagePreference,
  expanded,
  onToggle,
}: {
  entry: GuideEntry;
  languagePreference: LanguagePreference;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const title = resolveBilingual(languagePreference, { en: entry.title, es: entry.title_es });
  const spiel = entry.spiel_text.trim()
    ? resolveBilingual(languagePreference, { en: entry.spiel_text, es: entry.spiel_text_es })
    : null;
  const note = entry.internal_note.trim()
    ? resolveBilingual(languagePreference, {
        en: entry.internal_note,
        es: entry.internal_note_es,
      })
    : null;

  return (
    <div className="app-card overflow-hidden p-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="app-focus-ring flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-primary">
              {entry.category.name}
            </span>
            {entry.entry_type === "REBUTTAL" && (
              <Badge tone="warning">{t("guideList.typeRebuttal")}</Badge>
            )}
          </div>
          <div className="truncate text-base font-semibold text-slate-900">{title.primary}</div>
          {title.secondary && (
            <div className="truncate text-sm text-slate-500">{title.secondary}</div>
          )}
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-slate-400 transition-transform duration-150",
            expanded && "rotate-180"
          )}
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <div className="space-y-4 border-t border-slate-200 px-4 py-4">
          {spiel && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-primary">
                {t("flowPlayer.sayThis")}
              </div>
              <div className="mt-1 max-w-prose">
                <BilingualBlock text={spiel} className="text-base leading-relaxed text-slate-900" />
              </div>
            </div>
          )}
          {note && (
            <div className="flex gap-2 rounded-md border border-warning/30 bg-warning-subtle p-3 text-sm text-slate-800">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
              <div className="max-w-prose">
                <div className="font-medium text-warning">{t("guideList.guidanceLabel")}</div>
                <BilingualBlock text={note} className="mt-0.5 leading-relaxed" />
              </div>
            </div>
          )}
          {!spiel && !note && (
            <p className="text-sm text-slate-500">{t("guideList.noContent")}</p>
          )}
          <div className="max-w-prose">
            <GuideNotesEditor entryId={entry.id} />
          </div>
        </div>
      )}
    </div>
  );
}
